import { Checkbox, Link, Spinner, Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/react";
import { UseQueryResult } from "@tanstack/react-query";
import { FormattedMessage } from "react-intl";

import { Scenario } from "@nens/ldo/api-v1";
import { removeNullUndefined } from "@nens/shared-utils";

interface ComparisonTableProps {
  scenariosResponse: UseQueryResult<Scenario, unknown>[];
  fields: string[];
  fieldTitles: Record<string, string> | null;
  disabledScenarios: Record<number, boolean>;
  setDisabledScenarios: (disabledScenarios: Record<number, boolean>) => void;
}

export function ComparisonTable({
  scenariosResponse,
  fields,
  fieldTitles,
  disabledScenarios,
  setDisabledScenarios,
}: ComparisonTableProps) {
  const clickScenarioCheckbox = (id: number, checked: boolean) => {
    const newDisabledScenarios: Record<number, boolean> = {
      ...disabledScenarios,
      [id]: !checked,
    };
    setDisabledScenarios(newDisabledScenarios);
  };

  const allLoaded = scenariosResponse.every((response) => response.isSuccess);
  const allIds = removeNullUndefined(scenariosResponse.map((response) => response.data?.id));

  const allEnabled = allLoaded && allIds.every((id) => !disabledScenarios[id]);
  const someEnabled = allLoaded && allIds.some((id) => !disabledScenarios[id]);

  const clickAllCheckbox = () => {
    if (!allLoaded) return;
    if (!allEnabled) {
      // Some or all disabled -- enable all
      setDisabledScenarios({});
    } else {
      // All enabled - disable all
      setDisabledScenarios(Object.fromEntries(allIds.map((id) => [id, true])));
    }
  };

  return (
    <Table variant="simple" colorScheme="bij12_1">
      <Thead>
        <Tr>
          <Th>
            <Checkbox
              isChecked={allEnabled}
              isIndeterminate={!allEnabled && someEnabled}
              onChange={clickAllCheckbox}
            />
          </Th>
          <Th>ID</Th>
          <Th>Status</Th>
          <Th>
            <FormattedMessage id="pages.scenario.compare.name_column" defaultMessage="Name" />
          </Th>
          {fields.map((field) => (
            <Th key={field}>{fieldTitles ? fieldTitles[field] ?? field : field}</Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {scenariosResponse.map((response, idx) => (
          <Tr key={idx}>
            {response.isSuccess ? (
              <>
                <Td>
                  <Checkbox
                    isChecked={!disabledScenarios[response.data.id]}
                    onChange={(e) => clickScenarioCheckbox(response.data.id, e.target.checked)}
                  />
                </Td>
                <Td>
                  <Link href={`/scenarios/${response.data.id}`} target="_blank">
                    {response.data.id}
                  </Link>
                </Td>
                <Td>{response.data.status}</Td>
                <Td>
                  <Link href={`/scenarios/${response.data.id}`} target="_blank">
                    {response.data.name}
                  </Link>
                </Td>
                {fields.map((field) => (
                  <Td key={field}>{response.data.meta[field] ?? "-"}</Td>
                ))}
              </>
            ) : (
              <Td>
                <Spinner />
              </Td>
            )}
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}
